import { useApolloClient, useQuery } from "@apollo/client";
import { Dictionary } from "lodash";
import differenceBy from "lodash/differenceBy";
import keyBy from "lodash/keyBy";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

import {
  UserAttributeCategoryFragment,
  UserAttributeFragment,
  UserFragment,
  UserListQuery,
  UserQuery,
  UserQueryVariables,
  UserRoleType,
} from "gql/graphql";
import useBurstQuery from "modules/hooks/use-burst-query";
import { useAuth } from "../auth-provider";
import USER_QUERY from "./networking/user";
import USER_LIST_QUERY from "./networking/user-list";
import { useUserAttributes } from "../user-attributes-provider";
import { isUserInteractive } from "modules/utils/Functions";

interface IContextProps {
  users: readonly UserFragment[];
  emailUsers: UserFragment[];
  exposedEmailUsers: UserFragment[];
  activeUsers: UserFragment[];
  nonEmailUsers: UserFragment[];
  nonEmailActiveUsers: UserFragment[];
  usersById: Dictionary<UserFragment | undefined>;
  getUsersById(ids: string[]): UserFragment[];
  isEmailUser: (id: string) => boolean;
  refreshUsers: (ids: string[]) => void;
  userAttributesByUserId: Record<string, IUserAttributes[]>;
}

interface IUserAttributes {
  userAttributeCategory: UserAttributeCategoryFragment;
  userAttributes: UserAttributeFragment[];
}

const UsersContext = createContext<IContextProps>({
  users: [],
  emailUsers: [],
  exposedEmailUsers: [],
  activeUsers: [],
  nonEmailUsers: [],
  nonEmailActiveUsers: [],
  usersById: {},
  getUsersById: () => [],
  isEmailUser: () => false,
  refreshUsers: () => {},
  userAttributesByUserId: {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const UsersProvider = ({ children }: IProviderProps) => {
  const { cache } = useApolloClient();
  const { me } = useAuth();
  const { userAttributesById, userAttributeCategoriesById } =
    useUserAttributes();
  const { isLoggedIn } = useAuth();
  const [userQueryAPI] = useBurstQuery<UserQuery, UserQueryVariables>(
    USER_QUERY,
  );
  const { data } = useQuery<UserListQuery>(USER_LIST_QUERY, {
    skip: !isLoggedIn,
  });

  const users = useMemo(() => data?.userList ?? [], [data]);

  const usersById = useMemo(
    (): Dictionary<UserFragment | undefined> =>
      users ? keyBy(users, "id") : {},
    [users],
  );

  const emailUsers = useMemo(
    () => users?.filter((user) => user.roleType === UserRoleType.Email) || [],
    [users],
  );

  const exposedEmailUsers = useMemo(
    () =>
      emailUsers.filter((user) => me?.emailUserIdList.includes(user.id)) || [],
    [emailUsers, me?.emailUserIdList],
  );

  const activeUsers = useMemo(
    () => users.filter((user) => user.active === true) || [],
    [users],
  );

  const nonEmailUsers = useMemo(
    () => users.filter((user) => isUserInteractive(user)) || [],
    [users],
  );

  const nonEmailActiveUsers = useMemo(
    () => activeUsers.filter((user) => isUserInteractive(user)) || [],
    [activeUsers],
  );

  const isEmailUser = useCallback(
    (userId: string) => usersById[userId]?.roleType === UserRoleType.Email,
    [usersById],
  );

  const getUsersById = useCallback(
    (ids: string[]): UserFragment[] =>
      ids.flatMap((id) => {
        const user = usersById[id];
        return user ? [user] : [];
      }),
    [usersById],
  );

  const userAttributesByUserId = useMemo((): Record<
    string,
    IUserAttributes[]
  > => {
    const result: Record<string, IUserAttributes[]> = {};
    users.forEach((user) => {
      const _userAttributeCategoryIds = new Set<string>();
      const userAttributes: IUserAttributes[] = [];
      const _userAttributes = user.userAttributeIdList.reduce<
        UserAttributeFragment[]
      >((acc, userAttributeId) => {
        const userAttribute = userAttributesById[userAttributeId];
        if (!userAttribute) return acc;
        _userAttributeCategoryIds.add(userAttribute.userAttributeCategoryId);
        acc.push(userAttribute);
        return acc;
      }, []);

      _userAttributeCategoryIds.forEach((id) => {
        const userAttributeCategory = userAttributeCategoriesById[id];
        if (userAttributeCategory) {
          userAttributes.push({
            userAttributeCategory,
            userAttributes: _userAttributes.filter(
              (userAttribute) =>
                userAttribute.userAttributeCategoryId ===
                userAttributeCategory.id,
            ),
          });
        }
      });
      result[user.id] = userAttributes;
    });

    return result;
  }, [userAttributeCategoriesById, userAttributesById, users]);

  const refreshUsers = useCallback(
    (ids: string[]) => {
      userQueryAPI({ variables: { id: ids } })
        .then((result) => {
          const users = result.data?.user;
          if (!users?.length) {
            return;
          }
          cache.updateQuery<UserListQuery>(
            {
              query: USER_LIST_QUERY,
            },
            (data) => {
              if (!data) {
                return data;
              }

              // Ensure new users that were just created are added to the UserListQuery.
              const newUsers = differenceBy(users, data.userList, "id");

              return {
                ...data,
                userList: [...data.userList, ...newUsers],
              };
            },
          );
        })
        .catch(() => {
          // best-effort refresh: on a failed fetch the cached list stays as-is
        });
    },
    [userQueryAPI, cache],
  );

  const value = useMemo<IContextProps>(
    () => ({
      users,
      emailUsers,
      exposedEmailUsers,
      activeUsers,
      nonEmailUsers,
      nonEmailActiveUsers,
      usersById,
      getUsersById,
      isEmailUser,
      refreshUsers,
      userAttributesByUserId,
    }),
    [
      users,
      emailUsers,
      exposedEmailUsers,
      activeUsers,
      nonEmailUsers,
      nonEmailActiveUsers,
      usersById,
      getUsersById,
      isEmailUser,
      userAttributesByUserId,
      refreshUsers,
    ],
  );

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
};

export const useUsers = () => useContext(UsersContext);
